import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';

import { regionSyncService } from '../services/RegionSyncService';
import { SyncConfig, SyncConfigForm } from '../types';
import { logger } from 'utils/logger';

const log = logger.getLogger('ConfigFormDialog');

interface ConfigFormDialogProps {
  open: boolean;
  config: SyncConfig | null;
  onClose: () => void;
  onSave: () => void;
}

const ENVIRONMENTS = [
  { value: 'prod', label: 'Production' },
  { value: 'staging', label: 'Staging' },
  { value: 'dev', label: 'Development' },
];

// Fallback GCP projects (used while API is loading or if it fails)
const FALLBACK_GCP_PROJECTS = [
  { value: 'listen-prod', label: 'listen-prod (Production US)' },
  { value: 'in-listen-prod', label: 'in-listen-prod (Production India)' },
  { value: 'listen-staging', label: 'listen-staging (Staging)' },
  { value: 'listen-175015', label: 'listen-175015 (Development)' },
];

// All known K8s secrets for the dropdown
const K8S_SECRETS = [
  { value: '', label: 'None (use username/password)' },
  { value: 'mysql-hundredx', label: 'mysql-hundredx (Production US)' },
  { value: 'mysql-hundredx-in', label: 'mysql-hundredx-in (Production India)' },
  { value: 'mysql-staging', label: 'mysql-staging (Staging)' },
  { value: 'mysql-dev', label: 'mysql-dev (Development)' },
  { value: 'mysql-cbre', label: 'mysql-cbre (CBRE)' },
  { value: '__custom__', label: 'Custom...' },
];

interface EnvironmentInfo {
  gcpProject: string;
  label: string;
  environment: string;
  k8sSecret: string;
  defaultHost: string;
  defaultPort: number;
  defaultDatabase: string;
  instances?: Array<{ instanceName: string; privateIp: string; databaseVersion: string; region: string }>;
}

const initialFormState: SyncConfigForm = {
  name: '',
  environment: 'staging',
  gcpProject: 'listen-staging',
  dbHost: '',
  dbPort: 3306,
  dbName: 'solutions',
  dbUsername: '',
  dbPassword: '',
  k8sSecretName: '',
  k8sSecretNamespace: 'default',
  useSsl: false,
  isActive: true,
};

const ConfigFormDialog: React.FC<ConfigFormDialogProps> = ({
  open,
  config,
  onClose,
  onSave
}) => {
  const [form, setForm] = useState<SyncConfigForm>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customK8sSecret, setCustomK8sSecret] = useState('');
  const [isCustomSecret, setIsCustomSecret] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; latency?: string } | null>(null);
  const [environments, setEnvironments] = useState<EnvironmentInfo[]>([]);
  const [loadingEnvs, setLoadingEnvs] = useState(false);

  const isEditing = config !== null;

  // Fetch environments from API on mount
  useEffect(() => {
    if (open && environments.length === 0) {
      setLoadingEnvs(true);
      regionSyncService.getEnvironments()
        .then((envs: EnvironmentInfo[]) => {
          if (envs && envs.length > 0) {
            setEnvironments(envs);
            // If creating new config, auto-fill from first matching environment
            if (!config) {
              const defaultEnv = envs.find(e => e.gcpProject === 'listen-staging') || envs[0];
              if (defaultEnv) {
                setForm(prev => ({
                  ...prev,
                  dbHost: defaultEnv.defaultHost || prev.dbHost,
                  dbPort: defaultEnv.defaultPort || prev.dbPort,
                  dbName: defaultEnv.defaultDatabase || prev.dbName,
                  k8sSecretName: defaultEnv.k8sSecret || prev.k8sSecretName,
                }));
              }
            }
          }
        })
        .catch(err => log.error('Failed to fetch environments:', err))
        .finally(() => setLoadingEnvs(false));
    }
  }, [open]);

  // Build GCP project dropdown from API data or fallback
  const gcpProjects = environments.length > 0
    ? environments.map(env => ({ value: env.gcpProject, label: `${env.gcpProject} (${env.label})` }))
    : FALLBACK_GCP_PROJECTS;

  useEffect(() => {
    if (config) {
      const secretName = config.k8sSecretName || '';
      const isKnown = K8S_SECRETS.some(s => s.value === secretName);
      setIsCustomSecret(!!secretName && !isKnown);
      setCustomK8sSecret(!!secretName && !isKnown ? secretName : '');

      setForm({
        name: config.name,
        displayName: config.displayName || '',
        description: config.description || '',
        environment: config.environment,
        gcpProject: config.gcpProject || '',
        dbHost: config.dbHost,
        dbPort: config.dbPort,
        dbName: config.dbName,
        dbUsername: config.dbUsername || '',
        dbPassword: '', // Don't populate password for security
        k8sSecretName: secretName,
        k8sSecretNamespace: config.k8sSecretNamespace || 'default',
        useSsl: config.useSsl ?? false,
        isActive: config.isActive,
      });
    } else {
      setForm(initialFormState);
      setIsCustomSecret(false);
      setCustomK8sSecret('');
    }
    setError(null);
    setTestResult(null);
  }, [config, open]);

  const handleChange = (field: keyof SyncConfigForm) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Auto-populate K8s secret, host, port, and database when GCP project changes
  const handleGcpProjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const gcpProject = event.target.value;
    const envInfo = environments.find(e => e.gcpProject === gcpProject);
    setForm(prev => ({
      ...prev,
      gcpProject,
      k8sSecretName: envInfo?.k8sSecret || '',
      dbHost: envInfo?.defaultHost || prev.dbHost,
      dbPort: envInfo?.defaultPort || prev.dbPort,
      dbName: envInfo?.defaultDatabase || prev.dbName,
    }));
    setIsCustomSecret(false);
    setCustomK8sSecret('');
    setTestResult(null);
  };

  // Handle K8s secret dropdown change
  const handleK8sSecretChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '__custom__') {
      setIsCustomSecret(true);
      setForm(prev => ({ ...prev, k8sSecretName: customK8sSecret }));
    } else {
      setIsCustomSecret(false);
      setCustomK8sSecret('');
      setForm(prev => ({ ...prev, k8sSecretName: value }));
    }
  };

  // Handle custom secret name input
  const handleCustomSecretChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCustomK8sSecret(value);
    setForm(prev => ({ ...prev, k8sSecretName: value }));
  };

  // Determine the dropdown value based on current state
  const k8sDropdownValue = isCustomSecret
    ? '__custom__'
    : (K8S_SECRETS.some(s => s.value === form.k8sSecretName) ? form.k8sSecretName : '');

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setTestResult(null);

      if (!form.dbHost.trim()) {
        setTestResult({ success: false, message: 'Database host is required' });
        return;
      }
      if (!form.k8sSecretName?.trim() && !form.dbUsername?.trim()) {
        setTestResult({ success: false, message: 'Username is required when not using K8s secrets' });
        return;
      }

      const result = await regionSyncService.testConnectionRaw({
        dbHost: form.dbHost,
        dbPort: form.dbPort,
        dbName: form.dbName,
        dbUsername: form.dbUsername,
        dbPassword: form.dbPassword,
        k8sSecretName: form.k8sSecretName,
        k8sSecretNamespace: form.k8sSecretNamespace,
        useSsl: form.useSsl,
      });

      setTestResult({
        success: result.success,
        message: result.success ? `Connected successfully${result.latency ? ` (${result.latency})` : ''}` : (result.message || 'Connection failed'),
        latency: result.latency,
      });
    } catch (err: any) {
      setTestResult({ success: false, message: err.message || 'Connection test failed' });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!form.name.trim()) {
        throw new Error('Name is required');
      }
      if (!form.environment) {
        throw new Error('Environment is required');
      }
      if (!form.dbHost.trim()) {
        throw new Error('Database host is required');
      }
      if (!form.k8sSecretName?.trim() && !form.dbUsername?.trim()) {
        throw new Error('Username is required when not using K8s secrets');
      }
      if (!form.k8sSecretName?.trim() && !isEditing && !form.dbPassword?.trim()) {
        throw new Error('Password is required when creating a new config');
      }

      if (isEditing && config) {
        await regionSyncService.updateConfig(config.id, form);
      } else {
        await regionSyncService.createConfig(form);
      }

      onSave();
    } catch (err: any) {
      log.error('Failed to save config:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit Configuration' : 'Add Configuration'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Configuration Name"
              value={form.name}
              onChange={handleChange('name')}
              fullWidth
              required
              placeholder="e.g., US STG"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              label="Environment"
              value={form.environment}
              onChange={handleChange('environment')}
              fullWidth
              required
            >
              {ENVIRONMENTS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              select
              label="GCP Project"
              value={form.gcpProject}
              onChange={handleGcpProjectChange}
              fullWidth
              disabled={loadingEnvs}
              helperText={loadingEnvs ? 'Loading environments...' : ''}
            >
              {gcpProjects.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 8 }}>
            <TextField
              label="Database Host"
              value={form.dbHost}
              onChange={handleChange('dbHost')}
              fullWidth
              required
              placeholder="e.g., 127.0.0.1 or Cloud SQL instance IP"
            />
          </Grid>

          <Grid size={{ xs: 4 }}>
            <TextField
              label="Port"
              type="number"
              value={form.dbPort}
              onChange={handleChange('dbPort')}
              fullWidth
              required
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Database Name"
              value={form.dbName}
              onChange={handleChange('dbName')}
              fullWidth
              required
              placeholder="e.g., solutions"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Authentication
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 8 }}>
            <TextField
              select
              label="K8s Secret Name (optional)"
              value={k8sDropdownValue}
              onChange={handleK8sSecretChange}
              fullWidth
              helperText={form.k8sSecretName ? `Using secret: ${form.k8sSecretName}` : 'Select a secret or use username/password below'}
            >
              {K8S_SECRETS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField
              label="K8s Namespace"
              value={form.k8sSecretNamespace}
              onChange={handleChange('k8sSecretNamespace')}
              fullWidth
              placeholder="default"
            />
          </Grid>

          {isCustomSecret && (
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Custom K8s Secret Name"
                value={customK8sSecret}
                onChange={handleCustomSecretChange}
                fullWidth
                required
                placeholder="e.g., mysql-my-custom-secret"
                helperText="Enter the name of your Kubernetes secret"
              />
            </Grid>
          )}

          <Grid size={{ xs: 6 }}>
            <TextField
              label="Username"
              value={form.dbUsername}
              onChange={handleChange('dbUsername')}
              fullWidth
              required={!form.k8sSecretName?.trim()}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Password"
              type="password"
              value={form.dbPassword}
              onChange={handleChange('dbPassword')}
              fullWidth
              required={!isEditing && !form.k8sSecretName?.trim()}
              placeholder={isEditing ? '(unchanged)' : ''}
              helperText={isEditing ? 'Leave blank to keep existing password' : ''}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.useSsl ?? false}
                  onChange={handleChange('useSsl')}
                />
              }
              label="Use SSL"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
              Disable when using Cloud SQL Proxy
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={handleChange('isActive')}
                />
              }
              label="Active"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleTestConnection}
                disabled={testingConnection || !form.dbHost.trim()}
                startIcon={testingConnection ? <CircularProgress size={16} /> : null}
                size="small"
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
              {testResult && (
                <Alert
                  severity={testResult.success ? 'success' : 'error'}
                  sx={{ flex: 1, py: 0 }}
                  onClose={() => setTestResult(null)}
                >
                  {testResult.message}
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {isEditing ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigFormDialog;
